import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle, ChevronDown, ChevronUp, FileText, Landmark, RefreshCw, Server, ShieldCheck } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { LoadingBlock } from "../components/Spinner";
import { PortalLink } from "../components/PortalLink";
import { IndexTabs } from "../components/Drawing";
import { relativeDue, secondaryBtn, fmtDateTime } from "./resilience/shared";
import { CONTROL_LABELS, TONE, Pill, sinceText, type DoraContext, type Tone } from "./dora/shared";
import { DoraIncidentsTab, INCIDENT_FILTERS, type IncidentFilter } from "./dora/DoraIncidentsTab";
import { DoraTestsTab, TEST_FILTERS, type TestFilter } from "./dora/DoraTestsTab";
import { DoraThirdPartiesTab, PROVIDER_FILTERS, type ProviderFilter } from "./dora/DoraThirdPartiesTab";

// DORA monitoring: the three ongoing registers of Regulation (EU) 2022/2554 — ICT incidents
// (Arts. 17–23), resilience testing (Arts. 24–27) and ICT third parties (Arts. 28–44) — and the
// hourly monitor that turns them into alerts. Writes need controls:write (canEdit); viewers see
// the same information read-only. Deep links: ?tab=incidents&incident=<id>, ?tab=tests&test=<id>,
// ?tab=third-parties&provider=<id>, each tab also taking ?filter= (the summary tiles set it).

interface DoraSummary {
  openIncidents: number;
  majorIncidents: number;
  overdueTests: number;
  criticalProviders: number;
  slaBreaches: number;
  reviewsDue: number;
  openAlerts: number;
  breaches: number;
}

interface MonitorAlert {
  id: string;
  kind: string;
  severity: "warning" | "breach";
  controlId: string;
  entityType: "incident" | "test" | "third_party" | "programme";
  entityId: string | null;
  title: string;
  detail: string;
  dueAt: string | null;
  status: "open" | "resolved";
  firstDetectedAt: string;
  lastSeenAt: string;
  escalatedAt: string | null;
  notifiedAt: string | null;
  resolvedAt: string | null;
}

interface MonitorRun {
  id: string;
  reason: string;
  status: "running" | "done" | "error";
  openAlerts: number;
  breaches: number;
  newAlerts: number;
  resolvedAlerts: number;
  testsFlipped: number;
  evidenceWritten: number;
  notified: boolean;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
}

interface MonitorState {
  open: MonitorAlert[];
  recentlyResolved: MonitorAlert[];
  lastRun: MonitorRun | null;
  activeRun: { id: string; startedAt: string } | null;
  downgraded: Array<{ controlId: string; restoreStatus: string; downgradedAt: string }>;
}

type TabId = "overview" | "incidents" | "tests" | "third-parties";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "incidents", label: "ICT incidents" },
  { id: "tests", label: "Resilience testing" },
  { id: "third-parties", label: "ICT third parties" },
];

const RUN_REASON_LABELS: Record<string, string> = {
  scheduled: "hourly schedule",
  manual: "a manual check",
  register_change: "a register change",
  deadline: "a reporting deadline",
};

const REGISTER_OF: Record<string, { tab: TabId; label: string }> = {
  "legal-dora-art17": { tab: "incidents", label: "ICT incidents" },
  "legal-dora-art24": { tab: "tests", label: "Resilience testing" },
  "legal-dora-art28": { tab: "third-parties", label: "ICT third parties" },
};

const ID_PARAM: Record<Exclude<TabId, "overview">, string> = { incidents: "incident", tests: "test", "third-parties": "provider" };

export function DoraMonitoringPage() {
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("resilience:write");
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<DoraSummary | null>(null);
  const [context, setContext] = useState<DoraContext | null>(null);
  const [notEnabled, setNotEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rawTab = searchParams.get("tab");
  const tab: TabId = TABS.some((t) => t.id === rawTab) ? (rawTab as TabId) : searchParams.get("incident") ? "incidents" : "overview";
  const filter = searchParams.get("filter");

  /** Switch tab (and optionally open a record or filter) in one history entry. */
  const go = useCallback((next: TabId, params: Record<string, string> = {}) => {
    const p = new URLSearchParams();
    if (next !== "overview") p.set("tab", next);
    for (const [k, v] of Object.entries(params)) p.set(k, v);
    setSearchParams(p);
  }, [setSearchParams]);

  const setParam = useCallback((k: string, v: string | null) => {
    const p = new URLSearchParams(searchParams);
    if (v) p.set(k, v); else p.delete(k);
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await api.get<DoraSummary>("/dora/summary"));
      setError(null);
    } catch (e) {
      if ((e as { status?: number })?.status === 403) setNotEnabled(true);
      else setError(apiErrorMessage(e, "Could not load the DORA summary"));
    }
  }, []);

  useEffect(() => { void loadSummary(); }, [loadSummary]);
  useEffect(() => {
    api.get<DoraContext>("/dora/context").then(setContext).catch(() => { /* the summary reports a refusal */ });
  }, []);

  if (notEnabled) {
    return (
      <div className="page-pad">
        <Header />
        <div style={{ ...card, ...text.body, padding: 24, color: theme.color.muted, marginTop: 20, maxWidth: 760, lineHeight: 1.6 }}>
          <p style={{ margin: "0 0 12px" }}>
            The DORA monitoring add-on keeps the three ongoing obligations under Regulation (EU) 2022/2554 under continuous watch: the Art. 19
            incident reporting clocks, the resilience testing programme and the ICT third-party register are checked hourly and at every deadline.
            Alerts go to Teams, email and webhooks, records are written to the Evidence Repository, and a missed obligation shows as a gap on your
            Legal obligations and compliance score until it is put right.
          </p>
          <p style={{ margin: 0 }}>Contact your consultant to switch the add-on on for your organisation.</p>
        </div>
      </div>
    );
  }

  const badge = (id: TabId) => {
    if (!summary) return 0;
    if (id === "overview") return summary.breaches;
    if (id === "incidents") return summary.majorIncidents;
    if (id === "tests") return summary.overdueTests;
    return summary.slaBreaches;
  };

  return (
    <div className="page-pad">
      <Header />

      <IndexTabs
        items={TABS.map((t) => { const n = badge(t.id); return { id: t.id, label: t.label, count: n > 0 ? n : undefined }; })}
        value={tab}
        onChange={(id) => go(id)}
        style={{ margin: "18px 0 20px" }}
      />

      {error && <div style={{ ...card, ...text.body, padding: 14, marginBottom: 16, color: theme.color.dangerText }}>{error}</div>}

      {tab === "overview" && <Overview summary={summary} canEdit={canEdit} go={go} onChanged={loadSummary} />}
      {tab === "incidents" && (
        <DoraIncidentsTab canEdit={canEdit} context={context} onChanged={loadSummary}
          openId={searchParams.get(ID_PARAM.incidents)} onOpen={(id) => setParam(ID_PARAM.incidents, id)}
          filter={(INCIDENT_FILTERS.includes(filter as IncidentFilter) ? filter : "open") as IncidentFilter} onFilter={(f) => setParam("filter", f)} />
      )}
      {tab === "tests" && (
        <DoraTestsTab canEdit={canEdit} context={context} onChanged={loadSummary}
          openId={searchParams.get(ID_PARAM.tests)} onOpen={(id) => setParam(ID_PARAM.tests, id)}
          filter={(TEST_FILTERS.includes(filter as TestFilter) ? filter : "all") as TestFilter} onFilter={(f) => setParam("filter", f)} />
      )}
      {tab === "third-parties" && (
        <DoraThirdPartiesTab canEdit={canEdit} context={context} onChanged={loadSummary}
          openId={searchParams.get(ID_PARAM["third-parties"])} onOpen={(id) => setParam(ID_PARAM["third-parties"], id)}
          filter={(PROVIDER_FILTERS.includes(filter as ProviderFilter) ? filter : "all") as ProviderFilter} onFilter={(f) => setParam("filter", f)} />
      )}
    </div>
  );
}

function Header() {
  return (
    <div>
      <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
        <ShieldCheck size={20} /> DORA monitoring
      </h1>
      <p style={{ ...pageSubtitle, maxWidth: 760 }}>
        Classify and report ICT incidents on the Art. 19 clocks, run the resilience testing programme and keep the register of ICT third-party providers — each cross-checked against your asset, supplier and risk registers (Regulation (EU) 2022/2554).
      </p>
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────

function Tile({ label, value, sub, tone, onClick }: { label: string; value: number; sub?: string; tone?: Tone; onClick: () => void }) {
  const hot = tone && value > 0 ? TONE[tone] : null;
  return (
    <button type="button" onClick={onClick} style={{ ...card, padding: "14px 16px", height: "100%", textAlign: "left", cursor: "pointer", font: "inherit", borderColor: hot ? hot.border : undefined }}>
      <div style={text.statLabel}>{label}</div>
      <div style={{ ...text.stat, color: hot ? hot.color : theme.color.heading, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>}
    </button>
  );
}

function Overview({ summary, canEdit, go, onChanged }: { summary: DoraSummary | null; canEdit: boolean; go: (t: TabId, p?: Record<string, string>) => void; onChanged: () => void }) {
  const [monitor, setMonitor] = useState<MonitorState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(async () => {
    try { const m = await api.get<MonitorState>("/dora/monitor"); setMonitor(m); setError(null); return m; }
    catch (e) { setError(apiErrorMessage(e, "Could not load the monitor")); return null; }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function checkNow() {
    setRunning(true); setError(null);
    try {
      await api.post("/dora/monitor/run");
      // The run is queued on the worker: poll briefly until it finishes.
      for (let n = 0; n < 10; n++) {
        await new Promise((r) => window.setTimeout(r, 2500));
        const m = await load();
        if (m && !m.activeRun) break;
      }
      onChanged();
    } catch (e) { setError(apiErrorMessage(e, "Could not start the check")); }
    finally { setRunning(false); }
  }

  function openAlert(a: MonitorAlert) {
    if (a.entityType === "incident" && a.entityId) go("incidents", { incident: a.entityId, filter: "all" });
    else if (a.entityType === "test" && a.entityId) go("tests", { test: a.entityId });
    else if (a.entityType === "third_party" && a.entityId) go("third-parties", { provider: a.entityId });
    else go(REGISTER_OF[a.controlId]?.tab ?? "tests");
  }

  if (!summary) return <LoadingBlock />;
  const groups = Object.entries(REGISTER_OF).map(([controlId, reg]) => ({ controlId, reg, alerts: (monitor?.open ?? []).filter((a) => a.controlId === controlId) }));
  const other = (monitor?.open ?? []).filter((a) => !REGISTER_OF[a.controlId]);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <Tile label="Open incidents" value={summary.openIncidents} sub="ICT incidents not yet closed" tone="warning" onClick={() => go("incidents", { filter: "open" })} />
        <Tile label="Major incidents" value={summary.majorIncidents} sub="open, major or possibly major" tone="danger" onClick={() => go("incidents", { filter: "major" })} />
        <Tile label="Overdue tests" value={summary.overdueTests} sub="past their scheduled date" tone="danger" onClick={() => go("tests", { filter: "overdue" })} />
        <Tile label="Critical providers" value={summary.criticalProviders} sub="support a critical function" onClick={() => go("third-parties", { filter: "critical" })} />
        <Tile label="SLA breaches" value={summary.slaBreaches} sub="providers breaching their SLA" tone="danger" onClick={() => go("third-parties", { filter: "sla" })} />
        <Tile label="Reviews due" value={summary.reviewsDue} sub="provider reviews within 30 days" tone="warning" onClick={() => go("third-parties", { filter: "review" })} />
      </div>

      {error && <div style={{ ...card, ...text.body, padding: 12, color: theme.color.dangerText }}>{error}</div>}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
        <div style={{ ...card, padding: 0, flex: "2 1 460px", minWidth: 0 }}>
          <div style={{ ...text.sectionTitle, margin: 0, padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <AlertTriangle size={15} /> Needs attention
            <span style={{ ...text.hint, fontWeight: 400 }}>{summary.openAlerts} open · {summary.breaches} breach{summary.breaches === 1 ? "" : "es"}</span>
          </div>
          {!monitor ? <LoadingBlock /> : monitor.open.length === 0 ? (
            <div style={{ ...text.body, padding: 20, color: theme.color.successText, display: "flex", alignItems: "center", gap: 8 }}><CheckCircle size={15} /> Nothing outstanding. Every clock, test and provider duty is on track.</div>
          ) : (
            <div>
              {[...groups, ...(other.length ? [{ controlId: "other", reg: { tab: "overview" as TabId, label: "Other" }, alerts: other }] : [])].filter((g) => g.alerts.length > 0).map((g) => (
                <div key={g.controlId}>
                  <div style={{ ...text.eyebrow, padding: "8px 18px", background: theme.color.panelAlt, display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span>{g.reg.label}</span>
                    <span>{g.alerts.length}</span>
                  </div>
                  {g.alerts.map((a) => {
                    const t = TONE[a.severity === "breach" ? "danger" : "warning"];
                    return (
                      <button key={a.id} type="button" onClick={() => openAlert(a)} style={{ all: "unset", cursor: "pointer", display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start", width: "calc(100% - 36px)" }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, background: t.border, flexShrink: 0 }} />
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={text.itemTitle}>{a.title}</span>
                            <Pill tone={a.severity === "breach" ? "danger" : "warning"}>{a.severity === "breach" ? "Breach" : "Warning"}</Pill>
                          </span>
                          <span style={{ ...text.hint, display: "block", marginTop: 2 }}>{a.detail}</span>
                        </span>
                        {a.dueAt && <span style={{ ...text.meta, fontWeight: 600, color: t.color, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
          {monitor && monitor.recentlyResolved.length > 0 && (
            <div style={{ padding: "10px 18px" }}>
              <button type="button" onClick={() => setShowResolved((v) => !v)} style={{ all: "unset", cursor: "pointer", ...text.hint, display: "inline-flex", alignItems: "center", gap: 4 }}>
                {showResolved ? <ChevronUp size={13} /> : <ChevronDown size={13} />}Resolved in the last 7 days ({monitor.recentlyResolved.length})
              </button>
              {showResolved && (
                <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 4 }}>
                  {monitor.recentlyResolved.map((a) => (
                    <li key={a.id} style={{ ...text.hint, display: "flex", gap: 8 }}>
                      <CheckCircle size={12} style={{ color: theme.color.successText, marginTop: 2, flexShrink: 0 }} />
                      <span style={{ flex: 1 }}>{a.title}</span>
                      <span style={{ whiteSpace: "nowrap" }}>{fmtDateTime(a.resolvedAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gap: 16, flex: "1 1 280px", minWidth: 0 }}>
          <div style={{ ...card, padding: 18 }}>
            <div style={{ ...text.sectionTitle, display: "flex", alignItems: "center", gap: 6 }}><RefreshCw size={14} />Monitor</div>
            {!monitor ? <div style={text.hint}>Loading…</div> : (
              <div style={{ ...text.hint, lineHeight: 1.6 }}>
                {monitor.activeRun || running ? <div style={{ color: "var(--c-accentText)", fontWeight: 600 }}>Checking now…</div> : null}
                {monitor.lastRun ? (
                  <>
                    <div>Last checked <strong style={{ color: theme.color.text }}>{sinceText(monitor.lastRun.completedAt ?? monitor.lastRun.startedAt)}</strong>, after {RUN_REASON_LABELS[monitor.lastRun.reason] ?? monitor.lastRun.reason}.</div>
                    <div>{monitor.lastRun.newAlerts} new, {monitor.lastRun.resolvedAlerts} resolved{monitor.lastRun.evidenceWritten ? `, ${monitor.lastRun.evidenceWritten} evidence record${monitor.lastRun.evidenceWritten === 1 ? "" : "s"} written` : ""}{monitor.lastRun.testsFlipped ? `, ${monitor.lastRun.testsFlipped} test${monitor.lastRun.testsFlipped === 1 ? "" : "s"} marked overdue` : ""}.</div>
                    {monitor.lastRun.status === "error" && <div style={{ color: theme.color.dangerText }}>The last check failed{monitor.lastRun.errorMessage ? `: ${monitor.lastRun.errorMessage}` : ""}.</div>}
                  </>
                ) : <div>Not run yet. It runs hourly, after every register change and at each reporting deadline.</div>}
              </div>
            )}
            {canEdit && (
              <button type="button" style={{ ...secondaryBtn, marginTop: 12 }} disabled={running || !!monitor?.activeRun} onClick={checkNow}>
                <RefreshCw size={12} style={{ verticalAlign: -2, marginRight: 6 }} />{running ? "Checking…" : "Check now"}
              </button>
            )}
          </div>

          <div style={{ ...card, padding: 18 }}>
            <div style={{ ...text.sectionTitle, display: "flex", alignItems: "center", gap: 6 }}><Landmark size={14} />Legal obligations held at gap</div>
            {!monitor ? null : monitor.downgraded.length === 0 ? (
              <div style={{ ...text.hint, color: theme.color.successText, display: "flex", gap: 6, alignItems: "center" }}><CheckCircle size={13} />None. No open breach is holding a DORA obligation down.</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {monitor.downgraded.map((d) => (
                  <div key={d.controlId} style={{ padding: "10px 12px", borderRadius: 3, background: TONE.danger.bg, border: `1px solid ${TONE.danger.border}` }}>
                    <div style={{ ...text.itemTitle, color: TONE.danger.color }}>{CONTROL_LABELS[d.controlId] ?? d.controlId}</div>
                    <div style={{ ...text.hint, color: theme.color.text, marginTop: 3 }}>
                      Held at GAP since {fmtDateTime(d.downgradedAt)} because a breach is open in {REGISTER_OF[d.controlId]?.label.toLowerCase() ?? "its register"}. It returns to {d.restoreStatus.toLowerCase().replace(/_/g, " ")} automatically once the breach is put right.
                    </div>
                    {REGISTER_OF[d.controlId] && (
                      <button type="button" onClick={() => go(REGISTER_OF[d.controlId].tab)} style={{ all: "unset", cursor: "pointer", ...text.hint, color: "var(--c-accentText)", marginTop: 6, display: "inline-block" }}>Go to {REGISTER_OF[d.controlId].label}</button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ ...text.meta, marginTop: 10 }}>A breach lowers the matching obligation on Legal obligations and in your compliance score. Warnings do not.</div>
            <div style={{ marginTop: 6 }}><PortalLink to="/legal" style={{ ...text.hint, color: "var(--c-accentText)" }}>Legal obligations</PortalLink></div>
          </div>
        </div>
      </div>

      <div style={{ ...card, padding: 0 }}>
        <div style={{ ...text.sectionTitle, margin: 0, padding: "14px 18px", borderBottom: "1px solid var(--c-border)" }}>What each register covers</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
          {([
            { tab: "incidents" as TabId, icon: <AlertTriangle size={15} />, title: "ICT incidents (Arts. 17–23)", body: "Classify each incident against the RTS criteria. A major one is reported in three stages: initial notification within 4 hours of classification (24 hours from detection at most), intermediate report within 72 hours, final report within one month." },
            { tab: "tests" as TabId, icon: <FileText size={15} />, title: "Resilience testing (Arts. 24–27)", body: "Each required test type at least yearly, covering every system that supports a critical or important function. Designated entities also run threat-led penetration testing every three years." },
            { tab: "third-parties" as TabId, icon: <Server size={15} />, title: "ICT third parties (Arts. 28–44)", body: "Every ICT provider in the register of information, with due diligence, the Art. 30 contract terms and, for critical functions, a documented exit strategy tested in the last 12 months. The Register of Information downloads as a workbook in the ITS templates." },
          ]).map((x) => (
            <button key={x.tab} type="button" onClick={() => go(x.tab)} style={{ all: "unset", cursor: "pointer", padding: "14px 18px", display: "flex", gap: 12, borderRight: "1px solid var(--c-border)" }}>
              <span style={{ color: "var(--c-accentText)", flexShrink: 0, marginTop: 1 }}>{x.icon}</span>
              <span>
                <span style={{ ...text.itemTitle, display: "block", marginBottom: 3 }}>{x.title}</span>
                <span style={{ ...text.hint, display: "block" }}>{x.body}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {canEdit && summary.openIncidents === 0 && summary.openAlerts === 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" style={btnPrimary} onClick={() => go("incidents")}>Record an ICT incident</button>
          <button type="button" style={secondaryBtn} onClick={() => go("tests")}>Plan the testing programme</button>
          <button type="button" style={secondaryBtn} onClick={() => go("third-parties")}>Build the provider register</button>
        </div>
      )}
    </div>
  );
}

